/**
 * Builds the Programs/PMC page's tab bar for the current tab/type route params,
 * each with its own active state and click-navigation target.
 * @param {object} params.history React Router history, used to navigate on tab click.
 * @param {string} params.tab Current tab route param.
 * @param {string} params.type Current type route param (e.g. "open", "table").
 * @param {object} params.detail Programme detail record.
 * @param {string|number} params.queryVersionId Version id from the query string, if any.
 * @param {string|number} params.queryProgrammeId Programme id from the query string, if any.
 * @param {boolean|null} params.canViewVersions Whether the user holds `pmcversionview` (null while loading).
 */
export function getProgramsTabs({
    history,
    tab,
    type,
    id,
    detail,
    queryVersionId,
    queryProgrammeId,
    canViewVersions,
}) {
    const tabs = [];

    /*
     * Shared by isDetailsActive/detailsIcon/detailsLabel below — the table view for
     * any of these three tabs is the "Programmes" listing that the Details tab covers.
     */
    const isDetailsTableView =
        (tab === "programme" ||
            tab === "auditTrail" ||
            tab === "shortCourse") &&
        type === "table";

    const isDetailsActive =
        ((tab === "programme" ||
            tab === "shortCourse") &&
            type === "open") ||
        (isDetailsTableView && tab === "programme") ||
        (tab === "versions" &&
            (type === "create" ||
                type === "details" ||
                type === "clone"));

    const detailsIcon = isDetailsTableView ? "books" : "info-circle";

    const detailsLabel = isDetailsTableView ? "Programmes" : "Details";

    tabs.push({
        key: "details",
        icon: detailsIcon,
        label: detailsLabel,
        active: isDetailsActive,
        onClick: () => {
            if (
                type === "table" &&
                (tab === "programme" ||
                    tab === "auditTrail" ||
                    tab === "shortCourse")
            ) {
                history.push(
                    "/program-management-center/programmes/programme/table"
                );
            } else if (
                tab === "programme" ||
                tab === "assigned-modules" ||
                (tab === "versions" &&
                    type === "table") ||
                tab === "features-benefits" ||
                tab === "softwareused" ||
                tab === "study_path" ||
                tab === "graduates" ||
                tab === "linked_programmes" ||
                tab === "programmeDates" ||
                tab === "resources" ||
                (tab === "auditTrail" &&
                    type === "open")
            ) {
                history.push(
                    `/program-management-center/programmes/programme/open/${
            queryProgrammeId ?? id
          }`
                );
            } else if (tab === "versions") {
                if (type === "create") {
                    history.push(
                        `/program-management-center/programmes/versions/create?programmeId=${encodeURIComponent(queryProgrammeId)}`
                    );
                } else if (type === "clone") {
                    history.push(
                        `/program-management-center/programmes/versions/clone?versionId=${encodeURIComponent(queryVersionId)}&programmeId=${encodeURIComponent(queryProgrammeId)}`
                    );
                } else {
                    history.push(
                        `/program-management-center/programmes/versions/details?versionId=${encodeURIComponent(queryVersionId)}&programmeId=${encodeURIComponent(queryProgrammeId)}`
                    );
                }
            } else {
                history.push(
                    `/program-management-center/programmes/shortCourse/open/${
            queryProgrammeId ?? id
          }`
                );
            }
        },
    });

    if (
        (tab === "programme" ||
            tab === "shortCourse" ||
            tab === "auditTrail") &&
        type === "table"
    ) {
        tabs.push({
            key: "shortcourse",
            icon: "book",
            label: "Short Course",
            active: tab === "shortCourse",
            onClick: () =>
                history.push(
                    "/program-management-center/programmes/shortCourse/table"
                ),
        });
    }

    /*
     * A specific version's own sub-pages (as opposed to the Versions list
     * itself or the "add version" form), which get their own Modules/Study
     * Path/Gradebook tabs alongside Details/Audit Trails.
     */
    const isVersionDetailContext =
        tab === "versions" &&
        type !== "table" &&
        type !== "create";

    if (isVersionDetailContext) {
        tabs.push({
            key: "modules",
            icon: "book",
            label: "Modules",
            active: type === "assigned-modules",
            onClick: () =>
                history.push(
                    `/program-management-center/programmes/versions/assigned-modules?type=table&versionId=${encodeURIComponent(queryVersionId)}&programmeId=${encodeURIComponent(queryProgrammeId)}`
                ),
        });

        tabs.push({
            key: "study-path",
            icon: "route",
            label: "Study Path",
            active: type === "study_path",
            onClick: () =>
                history.push(
                    `/program-management-center/programmes/versions/study_path?type=table&versionId=${encodeURIComponent(queryVersionId)}&programmeId=${encodeURIComponent(queryProgrammeId)}`
                ),
        });

        tabs.push({
            key: "gradebook",
            icon: "table-pivot",
            label: "Gradebook",
            active: type === "gradebook",
            onClick: () =>
                history.push(
                    `/program-management-center/programmes/versions/gradebook?versionId=${encodeURIComponent(queryVersionId)}&programmeId=${encodeURIComponent(queryProgrammeId)}`
                ),
        });

        tabs.push({
            key: "linked",
            icon: "books",
            label: "Linked Programmes",
            active: type === "linked_programmes",
            showDivider: true,

            onClick: () =>
                history.push(
                    `/program-management-center/programmes/versions/linked_programmes/${encodeURIComponent(queryProgrammeId)}?type=table&versionId=${encodeURIComponent(queryVersionId)}&programmeId=${encodeURIComponent(queryProgrammeId)}`
                ),
        });

        tabs.push({
            key: "graduates",
            icon: "user-graduate",
            label: "Graduates",
            active: type === "graduates",
            onClick: () =>
                history.push(
                    `/program-management-center/programmes/versions/graduates/${encodeURIComponent(queryProgrammeId)}?type=table&versionId=${encodeURIComponent(queryVersionId)}&programmeId=${encodeURIComponent(queryProgrammeId)}`
                ),
        });
    }

    const showFeatures =
        ((tab === "programme" ||
            tab === "auditTrail" ||
            tab === "shortCourse") &&
            type === "open") ||
        ((tab === "assigned-modules" ||
            tab === "versions" ||
            tab === "graduates" ||
            tab === "resources" ||
            tab === "shortCourse-resources" ||
            tab === "linked_programmes" ||
            tab === "softwareused" ||
            tab === "study_path" ||
            tab === "features-benefits" ||
            tab === "programmeDates" ||
            tab === "shortCourseFeatures-benefits" ||
            tab === "shortCoursetopics" ||
            tab === "shortCourseOutcomes" ||
            tab === "courseDates") &&
            type === "table") ||
        (tab === "features-benefits" &&
            type === "re-arrange") ||
        (tab ===
            "shortCourseFeatures-benefits" &&
            type === "re-arrange") ||
        (tab === "shortCourseAuditTrail" &&
            type === "open") ||
        (tab === "study_path" &&
            (type === "create" ||
                type === "open"));

    if (showFeatures) {
        tabs.push({
            key: "features",
            icon: "layer-group",
            label: "Features & Benefits",
            active:
                tab === "features-benefits" ||
                tab ===
                    "shortCourseFeatures-benefits",
            onClick: () => {
                if (
                    ((tab === "shortCourse" ||
                        tab ===
                            "shortCourseAuditTrail") &&
                        type === "open") ||
                    ((tab ===
                        "shortCoursetopics" ||
                        tab ===
                            "shortCourse-resources" ||
                        tab ===
                            "shortCourseOutcomes" ||
                        tab === "courseDates" ||
                        tab ===
                            "shortCourseFeatures-benefits") &&
                        type === "table")
                ) {
                    history.push(
                        `/program-management-center/programmes/shortCourseFeatures-benefits/table/${id}`
                    );
                } else {
                    history.push(
                        `/program-management-center/programmes/features-benefits/table/${id}`
                    );
                }
            },
        });
    }

    const showSoftware =
        ((tab === "programme" ||
            tab === "auditTrail") &&
            type === "open") ||
        ((tab === "versions" ||
            tab === "graduates" ||
            tab === "resources" ||
            tab === "softwareused" ||
            tab === "linked_programmes" ||
            tab === "study_path" ||
            tab === "features-benefits" ||
            tab === "programmeDates") &&
            type === "table") ||
        (tab === "features-benefits" &&
            type === "re-arrange");

    if (showSoftware) {
        tabs.push({
            key: "software",
            icon: "computer",
            label: "Software",
            active: tab === "softwareused",
            onClick: () => {
                history.push(
                    `/program-management-center/programmes/softwareused/table/${id}`
                );
            },
        });
    }

    const showShortCourseTopicsAndOutcomes =
        ((tab === "shortCourse" ||
            tab === "shortCourseAuditTrail") &&
            type === "open") ||
        ((tab === "courseDates" ||
            tab === "shortCoursetopics" ||
            tab === "shortCourseOutcomes" ||
            tab === "shortCourse-resources" ||
            tab === "shortCourseFeatures-benefits") &&
            type === "table") ||
        (tab === "shortCourseFeatures-benefits" &&
            type === "re-arrange");

    if (showShortCourseTopicsAndOutcomes) {
        tabs.push({
            key: "topics",
            icon: "books",
            label: "Topics",
            active: tab === "shortCoursetopics",
            onClick: () =>
                history.push(
                    `/program-management-center/programmes/shortCoursetopics/table/${id}`
                ),
        });

        tabs.push({
            key: "outcomes",
            icon: "chart-pie-alt",
            label: "Outcomes",
            active: tab === "shortCourseOutcomes",
            onClick: () =>
                history.push(
                    `/program-management-center/programmes/shortCourseOutcomes/table/${id}`
                ),
        });
    }

    const showResources =
        ((tab === "programme" ||
            tab === "auditTrail" ||
            tab === "shortCourse") ||
            (tab === "shortCourseAuditTrail" &&
                type === "open") ||
            tab === "programmeDates" ||
            tab === "linked_programmes" ||
            tab === "resources" ||
            tab === "graduates" ||
            tab === "assigned-modules" ||
            tab === "features-benefits" ||
            tab ===
                "shortCourseFeatures-benefits" ||
            tab === "shortCoursetopics" ||
            tab === "softwareused" ||
            tab === "shortCourseOutcomes" ||
            tab === "shortCourse-resources" ||
            (tab === "versions" &&
                type === "table")) &&
        !(
            tab === "shortCourse" &&
            type === "table"
        ) &&
        !(
            tab === "programme" &&
            type === "table"
        ) &&
        !(
            tab === "auditTrail" &&
            type === "table"
        );

    if (showResources) {
        tabs.push({
            key: "resources",
            icon: "book-arrow-right",
            label: "Resources",
            active:
                tab === "resources" ||
                tab === "shortCourse-resources",
            onClick: () => {
                if (
                    ((tab === "shortCourse" ||
                        tab ===
                            "shortCourseAuditTrail") &&
                        type === "open") ||
                    ((tab ===
                        "shortCoursetopics" ||
                        tab ===
                            "shortCourseOutcomes" ||
                        tab === "courseDates" ||
                        tab ===
                            "shortCourseFeatures-benefits" ||
                        tab ===
                            "shortCourse-resources") &&
                        type === "table") ||
                    tab ===
                        "shortCourseFeatures-benefits"
                ) {
                    history.push(
                        `/program-management-center/programmes/shortCourse-resources/table/${id}`
                    );
                } else {
                    history.push(
                        `/program-management-center/programmes/resources/table/${id}`
                    );
                }
            },
        });
    }

    const showVersions =
        (((tab === "features-benefits" ||
            tab === "graduates" ||
            tab === "resources" ||
            tab === "softwareused" ||
            tab === "versions" ||
            tab === "assigned-modules" ||
            tab === "study_path" ||
            tab === "programmeDates" ||
            tab === "linked_programmes") &&
            type === "table") ||
            (tab === "features-benefits" &&
                type === "re-arrange") ||
            ((tab === "programme" ||
                tab === "auditTrail") &&
                type === "open") ||
            (tab === "study_path" &&
                (type === "re-arrange" ||
                    type === "open" ||
                    type === "create"))) &&
        canViewVersions !== false &&
        /*
         * Already on a Versions route -> the tab must stay visible even before
         * `detail` (and its EnableStudypathLinkReReg flag) has loaded, so the tab bar
         * doesn't flicker the current tab away while the Versions screen itself is
         * still loading. From elsewhere, the fetched flag is still the source of truth.
         */
        (tab === "versions" ||
            // 1 = study-path-linked re-registration is enabled for this programme
            Number(detail?.EnableStudypathLinkReReg) === 1);

    if (showVersions) {
        tabs.push({
            key: "versions",
            icon: "grid-2",
            label: "Versions",
            active: tab === "versions",
            onClick: () =>
                history.push(
                    `/program-management-center/programmes/versions/table/${id}`
                ),
            showDivider: true,
        });
    }

    const showLinked =
        ((tab === "programme" ||
            tab === "auditTrail") ||
            (tab !==
                "shortCourseAuditTrail" &&
                tab !== "shortCourse" &&
                type === "open") ||
            tab === "programmeDates" ||
            tab === "linked_programmes" ||
            tab === "resources" ||
            tab === "graduates" ||
            (tab === "versions" &&
                type === "table") ||
            tab === "features-benefits" ||
            tab === "softwareused") &&
        !(
            tab === "shortCourse" &&
            type === "open"
        ) &&
        !(
            tab === "programme" &&
            type === "table"
        ) &&
        !(
            tab === "auditTrail" &&
            type === "table"
        ) &&
        !(
            tab === "versions" &&
            type === "create"
        ) &&
        !isVersionDetailContext;

    if (showLinked) {
        tabs.push({
            key: "linked",
            icon: "books",
            label: "Linked Programmes",
            active: tab === "linked_programmes",
            onClick: () =>
                history.push(
                    `/program-management-center/programmes/linked_programmes/table/${id}`
                ),
        });
    }

    const showGraduates =
        // Same already-on-this-route safeguard as showVersions, above.
        (tab === "versions" ||
            // 1 = study-path-linked re-registration is enabled for this programme
            Number(detail?.EnableStudypathLinkReReg) === 1) &&
        (((tab === "programme" ||
            tab === "auditTrail") &&
            type === "open") ||
            ((tab === "graduates" ||
                tab === "programmeDates" ||
                tab === "resources" ||
                tab === "linked_programmes" ||
                tab === "softwareused" ||
                tab === "assigned-modules" ||
                tab === "versions" ||
                tab === "features-benefits") &&
                type === "table") ||
            (tab === "features-benefits" &&
                type === "re-arrange")) &&
        tab !== "shortCourse";

    if (showGraduates) {
        tabs.push({
            key: "graduates",
            icon: "user-graduate",
            label: "Graduates",
            active: tab === "graduates",
            onClick: () =>
                history.push(
                    `/program-management-center/programmes/graduates/table/${id}`
                ),
        });
    }

    if (
        !(
            tab === "versions" &&
            type === "create"
        )
    ) {
        tabs.push({
            key: "audit",
            icon: "history",
            label: "Audit Trails",
            showDivider: !isDetailsTableView,
            active:
                tab === "auditTrail" ||
                tab ===
                    "shortCourseAuditTrail" ||
                type === "auditTrail",
            onClick: () => {
                if (
                    type === "table" &&
                    (tab === "programme" ||
                        tab === "auditTrail" ||
                        tab === "shortCourse")
                ) {
                    history.push(
                        "/program-management-center/programmes/auditTrail/table"
                    );
                } else if (
                    ((tab === "programme" ||
                        tab === "auditTrail") &&
                        type === "open") ||
                    ((tab ===
                        "features-benefits" ||
                        tab === "softwareused" ||
                        tab === "versions" ||
                        tab ===
                            "programmeDates" ||
                        tab ===
                            "linked_programmes" ||
                        tab === "graduates") &&
                        type === "table") ||
                    (tab === "study_path" &&
                        (type ===
                            "re-arrange" ||
                            type === "create" ||
                            type === "open" ||
                            type === "table"))
                ) {
                    history.push(
                        `/program-management-center/programmes/auditTrail/open/${
                            queryProgrammeId ?? id
                        }`
                    );
                } else if (
                    tab === "versions" &&
                    type !== "table"
                ) {
                    history.push(
                        `/program-management-center/programmes/versions/auditTrail?type=table&versionId=${encodeURIComponent(queryVersionId)}&programmeId=${encodeURIComponent(queryProgrammeId)}`
                    );
                } else {
                    history.push(
                        `/program-management-center/programmes/shortCourseAuditTrail/open/${id}`
                    );
                }
            },
        });
    }

    return tabs;
}
